// src/utils/roles.js
import { logger } from "./logger";

// Blacklist: environment markers that are part of a tenant name, not a role.
// Must match the set in userAdmin-fe/src/utils/roles.ts to keep tenant parsing consistent.
const ENV_SUFFIXES = new Set(["dev", "test", "prod"]);

export function extractTenantsAndApiKeys(claims) {
  const tenantApiKeyMap = {};
  const tenantRoleMap = {};
  const tenants = [];
  const shouldParseRole = process.env.REACT_APP_ENABLE_BEARER_TOKEN !== "false";

  if (claims && typeof claims === "object") {
    // api_keys can be a string "tenant-role---apikey" or an array of such strings
    // Example: "cogsol-productdev-user---VzwpB7xGutw"
    // Where: tenant = "cogsol-productdev", role = "user", apikey = "VzwpB7xGutw"
    const apiKeysClaim = claims.api_keys;
    const apiKeyItems = Array.isArray(apiKeysClaim)
      ? apiKeysClaim
      : typeof apiKeysClaim === "string"
      ? [apiKeysClaim]
      : [];

    for (const item of apiKeyItems) {
      let itemStr = null;

      if (typeof item === "string") {
        itemStr = item;
      } else if (item && typeof item === "object" && item.val) {
        itemStr = String(item.val);
      }

      if (itemStr) {
        // Split by "---" to separate tenant-role from apikey
        const parts = itemStr.split("---");
        if (parts.length === 2) {
          const tenantRole = parts[0];
          const apiKey = parts[1];

          // Parsing aligned with userAdmin-fe/src/utils/roles.ts
          let tenant = tenantRole;
          let role = "";

          if (shouldParseRole) {
            const lastHyphenIndex = tenantRole.lastIndexOf("-");
            if (lastHyphenIndex !== -1) {
              const candidateTenant = tenantRole.substring(0, lastHyphenIndex);
              const candidateRole = tenantRole.substring(lastHyphenIndex + 1).toLowerCase();

              // Split unless the suffix is an environment marker (part of the tenant name).
              // Roles are dynamic and validated by the backend, so any other suffix is accepted as-is.
              const canSplitByRole = !ENV_SUFFIXES.has(candidateRole);

              if (canSplitByRole) {
                tenant = candidateTenant;
                role = candidateRole;
              }
            }
          }

          tenantApiKeyMap[tenant] = apiKey;
          if (role) {
            tenantRoleMap[tenant] = role;
          }
          if (!tenants.includes(tenant)) tenants.push(tenant);

          logger.debug(`[Roles] Extracted - Tenant: ${tenant}, Role: ${role || "(none)"}, ApiKey: ${apiKey ? '***' : 'none'}`);
        }
      }
    }

    // Store tenants, tenant-role mapping, and groups
    if (tenants.length) {
      sessionStorage.setItem("userTenants", JSON.stringify(tenants));
    }
    if (Object.keys(tenantRoleMap).length > 0) {
      sessionStorage.setItem("tenantRoleMap", JSON.stringify(tenantRoleMap));
    }

    const groups =
      (Array.isArray(claims.groups) && claims.groups) ||
      (typeof claims.groups === "string" ? [claims.groups] : []);
    if (groups.length) {
      sessionStorage.setItem("userGroups", JSON.stringify(groups));
    }
  }

  return { tenantApiKeyMap, tenantRoleMap, tenants };
}

/**
 * Get the currently selected tenant
 * @returns {string} The current tenant (e.g., "cogsol-productdev")
 */
export function getCurrentTenant() {
  try {
    const storedValue = sessionStorage.getItem("userTenant");
    if (!storedValue || storedValue === "undefined" || storedValue === "null") {
      return "";
    }
    return JSON.parse(storedValue);
  } catch (error) {
    logger.error("[Roles] Error parsing userTenant:", error);
    return "";
  }
}

/**
 * Set the currently selected tenant
 * @param {string} tenant - The tenant to set (e.g., "cogsol-productdev")
 */
export function setCurrentTenant(tenant) {
  sessionStorage.setItem("userTenant", JSON.stringify(tenant));
}

/**
 * Set user tenants in sessionStorage
 * @param {string[]} tenants - Array of tenant names
 */
export function setUserTenants(tenants) {
  sessionStorage.setItem("userTenants", JSON.stringify(tenants));
}

/**
 * Determine and set tenant based on available tenants
 * @param {string[]} availableTenants - Array of available tenant names
 * @returns {string} The selected tenant
 */
export function determineAndSetTenant(availableTenants) {
  const currentTenant = getCurrentTenant();

  let selectedTenant = currentTenant;
  if (!availableTenants.includes(currentTenant) && availableTenants.length > 0) {
    selectedTenant = availableTenants[0];
    setCurrentTenant(selectedTenant);
  }

  return selectedTenant;
}

/**
 * Get the selected API key based on current tenant
 * @param {Object} tenantApiKeys - Object mapping tenant names to API keys
 * @returns {string|undefined} The API key for the selected tenant
 */
export function getSelectedTenantApiKey(tenantApiKeys) {
  if (!tenantApiKeys || typeof tenantApiKeys !== "object") return undefined;
  try {
    const tenant = getCurrentTenant();
    return tenantApiKeys[tenant];
  } catch (error) {
    logger.error("[Roles] Error getting selected tenant API key:", error);
    return undefined;
  }
}

// ==================
// UTILITY FUNCTIONS
// ==================

/**
 * Get all available tenants from sessionStorage
 * @returns {string[]} Array of tenant names
 */
export function getUserTenants() {
  try {
    const stored = sessionStorage.getItem("userTenants");
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logger.error("[Roles] Error parsing userTenants:", error);
    return [];
  }
}

/**
 * Get the role associated with a specific tenant
 * @param {string} tenant - The tenant name (e.g., "cogsol-productdev")
 * @returns {string} The role for that tenant (e.g., "user", "admin")
 */
export function getRoleForTenant(tenant) {
  try {
    const stored = sessionStorage.getItem("tenantRoleMap");
    if (!stored) return "";
    const tenantRoleMap = JSON.parse(stored);
    return tenantRoleMap[tenant] || "";
  } catch (error) {
    logger.error("[Roles] Error parsing tenantRoleMap:", error);
    return "";
  }
}

/**
 * Get the user role for the currently selected tenant
 * @returns {string} The current user role (e.g., "user", "admin")
 */
export function getCurrentUserRole() {
  const currentTenant = getCurrentTenant();
  return getRoleForTenant(currentTenant);
}

export function normalizeEnvironment(envValue = process.env.REACT_APP_ENVIRONMENT) {
  const env = (envValue || "").toLowerCase();
  if (env === "dev" || env === "development") return "development";
  if (env === "test" || env === "testing") return "testing";
  if (env === "prod" || env === "imp" || env === "implantation-production")
    return "implantation-production";
  return env;
}

export function isTenantValidForEnvironment(tenant) {
  const isLocalEnv = process.env.REACT_APP_IS_LOCAL_ENV === "true";
  const env = normalizeEnvironment();
  const t = (tenant || "").toLowerCase();

  if (!t) return false;
  if (!env || isLocalEnv) return true;

  if (env === "development") return t.includes("productdev");
  if (env === "testing") return t.includes("producttest");

  // implantation-production y otros: excluir dev y test
  return !t.includes("productdev") && !t.includes("producttest");
}
